/* eslint-disable @typescript-eslint/no-explicit-any */
import { StockColor, StockDetail } from '.';
import {
  arrayColumn,
  formatAMPM,
  getShortDate,
} from '../../Utils/UtilFunctions';
import { addCommasToNumber } from '../../Utils/CommonUtils';
import { getMappedValue } from './goldSilverChart';

const Color = require('color');
const xAxis = (color: StockColor) => {
  return {
    crosshair: {
      color: color.gradient,
      dashStyle: 'LongDash',
    },
    type: 'datetime',
    main: '',
    lineColor: '#DADFEA',
    tickLength: 0,
    // Xaxis labels color white
    labels: {
      style: {
        color: '#9092A3',
        font: 'Gilroy',
      },
      enabled: false,
    },
  };
};

const getXAxisConfig = (color: StockColor) => {
  return { ...xAxis(color) };
};

export const getXAxisConfigDaily = (
  color: StockColor,
  stockData: Array<StockDetail>
): any => {
  const date = new Date(stockData[0]?.[0] || 0); // opening time/bell to be changed to 9:30 IST
  const closingOffset = 6 * 60 * 60 * 1000 + 15 * 60 * 1000; // 6 hours 15minutes converted to milliseconds
  const obJToReturn = {
    ...xAxis(color),
    min: date.getTime(),
    max: date.getTime() + closingOffset, // TODO: make this 3:30pm IST
  };
  return { ...obJToReturn };
};

const getCommonOptions = (stockColor: StockColor) => {
  return {
    chart: {
      type: 'area',
    },
    title: {
      text: '',
      color: 'white',
    },
    time: {
      timezoneOffset: -330, // -5:30 hrs India is East to UTC
    },
    credits: false,
    legend: {
      enabled: false,
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, Color(stockColor.gradient).alpha(0.4).string()],
            [1, Color(stockColor.gradient).alpha(0).string()],
          ],
        },
        marker: {
          enabled: false,
          states: {
            hover: {
              fillColor: stockColor.line,
              lineColor: '#ffffff',
              lineWidth: 3,
              radius: 8,
            },
          },
        },
        lineWidth: 2,
        lineColor: stockColor.line,
        threshold: null,
      },
      series: {
        turboThreshold: 5000,
      },
    },
    yAxis: {
      visible: true,
      opposite: true,
      title: {
        text: undefined,
      },
      gridLineWidth: 0,
      lineWidth: 1,
      lineColor: '#DADFEA',
      tickLength: 5,
      tickWidth: 1,
      tickColor: '#DADFEA',
      crosshair: {
        color: stockColor.gradient,
        dashStyle: 'LongDash',
      },
      labels: {
        enabled: true,
        style: {
          color: '#9092A3',
          font: 'Gilroy',
        },
        formatter: function (
          this: Highcharts.AxisLabelsFormatterContextObject
        ) {
          return '₹' + addCommasToNumber(Math.floor(Number(this.value)));
        },
      },
    },
  };
};
export const options = (
  stockData: Array<StockDetail>,
  stockColor: StockColor,
  showTimeInToolTip: boolean
): any => ({
  ...getCommonOptions(stockColor),
  tooltip: {
    animation: true,
    borderRadius: 20,
    backgroundColor: 'white',
    borderWidth: 0,
    padding: 12,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      const timestamp = stockData[this.key as number]?.[0];
      const dateObj = new Date(timestamp as number);
      const time = formatAMPM(dateObj);
      return `<span style="color: var(--font-200);">${getShortDate(
        dateObj
      )}</span><br/><br/><strong style="color: var(--font-500);">₹${addCommasToNumber(
        this.y
      )}${
        showTimeInToolTip
          ? `</strong><span style="color: var(--font-200); padding-left:8px;">${' '}${time}</span>`
          : ``
      }`;
    },
    style: { opacity: 1 },
  },
  xAxis: getXAxisConfig(stockColor),
  series: [
    {
      data: arrayColumn([...stockData], 1),
    },
  ],
});

export const optionsDaily = (
  stockData: Array<StockDetail>,
  stockColor: StockColor,
  showTimeInToolTip: boolean
): any => ({
  ...getCommonOptions(stockColor),
  tooltip: {
    animation: true,
    borderRadius: 20,
    backgroundColor: 'white',
    borderWidth: 0,
    padding: 12,
    formatter(this: Highcharts.TooltipFormatterContextObject) {
      const timestamp = this.x;
      const dateObj = new Date(timestamp as number);
      const time = formatAMPM(dateObj);
      return `<span style="color: var(--font-200);">${getShortDate(
        dateObj
      )}</span><br/><br/><strong style="color: var(--font-500);">₹${addCommasToNumber(
        this.y
      )}${
        showTimeInToolTip
          ? `</strong><span style="color: var(--font-200); padding-left:8px;">${' '}${time}</span>`
          : ``
      }`;
    },
    style: { opacity: 1 },
  },
  xAxis: getXAxisConfigDaily(stockColor, stockData),
  series: [
    {
      data: [...stockData],
    },
  ],
});

export const optionsTable = (
  stockData: Array<StockDetail>,
  stockColor: StockColor,
  height?: number,
  width?: number
): any => {
  return {
    ...getCommonOptions(stockColor),
    chart: {
      type: 'area',
      height: height ? height : 120,
      width: width ? width : 300,
    },
    xAxis: {
      ...getXAxisConfigDaily(stockColor, stockData),
      visible: false,
      crosshair: undefined,
    },
    yAxis: {
      title: {
        text: undefined,
      },
      lineWidth: 0,
      gridLineWidth: 0,
      gridLineColor: 'white',
      plotLines: [
        {
          color: '#e0e0e0',
          width: 1,
          zIndex: 20,
          dashStyle: 'Dash',
        },
      ],
      labels: {
        enabled: false,
        visible: false,
      },
      visible: true,
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      area: {
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
              lineWidth: 2,
            },
          },
        },
        lineWidth: 2,
        lineColor: stockColor.line,
        threshold: null,
        fillOpacity: 0,
      },
      series: {
        states: {
          hover: {
            enabled: true,
            lineWidth: 2,
          },
        },
      },
    },
    series: [
      {
        data: [...stockData],
      },
    ],
  };
};

export const optionsHomepage = (
  stockData: Array<StockDetail>,
  stockColor: StockColor,
  prevClose: number,
  todayHigh: number,
  todayLow: number,
  height?: number
): any => {
  const upDateMinY = prevClose < todayLow ? prevClose : null;
  const upDateMaxY = prevClose > todayHigh ? prevClose : null;

  return {
    ...getCommonOptions(stockColor),
    chart: {
      type: 'area',
      height: height ? height : 120,
    },
    xAxis: {
      ...getXAxisConfigDaily(stockColor, stockData),
      visible: false,
      crosshair: undefined,
    },
    yAxis: {
      title: {
        text: undefined,
      },
      lineWidth: 0,
      gridLineWidth: 0,
      gridLineColor: 'white',
      plotLines: [
        {
          color: '#e0e0e0',
          width: 1,
          value: prevClose, // got to add prevclose here and handle min max too
          zIndex: 20,
          dashStyle: 'Dash',
        },
      ],
      labels: {
        enabled: false,
        visible: false,
      },
      min: upDateMinY,
      max: upDateMaxY,
      visible: true,
    },
    tooltip: {
      enabled: false,
    },
    plotOptions: {
      area: {
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
              lineWidth: 2,
            },
          },
        },
        lineWidth: 2,
        lineColor: stockColor.line,
        threshold: null,
        fillOpacity: 0,
      },
      series: {
        states: {
          hover: {
            enabled: true,
            lineWidth: 2,
          },
        },
      },
    },
    series: [
      {
        data: [...stockData],
      },
    ],
  };
};

export const optionsGoldSilver = (
  goldSilverData: Array<StockDetail>,
  color: StockColor,
  showTimeInToolTip: boolean,
  gram: string,
  carat?: string
): any => {
  const grams = getMappedValue[gram] || 1;
  let rate = `${gram}:`;
  if (carat) {
    const displayUnit = gram.split('g')[0] + ' g';
    rate = carat
      ? `${carat.toUpperCase()}/${displayUnit}:`
      : `${displayUnit}: `;
  } else {
    if (gram == '1gm') rate = '1 g:';
    else rate = '1 kg:';
  }
  return {
    ...getCommonOptions(color),
    yAxis: {
      visible: true,
      opposite: true,
      title: {
        text: undefined,
      },
      gridLineWidth: 0,
      lineWidth: 1,
      lineColor: '#DADFEA',
      tickLength: 5,
      tickWidth: 1,
      tickColor: '#DADFEA',
      crosshair: {
        color: color.gradient,
        dashStyle: 'LongDash',
      },
      labels: {
        enabled: true,
        style: {
          color: '#9092A3',
          font: 'Gilroy',
        },
        formatter: function (
          this: Highcharts.AxisLabelsFormatterContextObject
        ) {
          return (
            '₹' + addCommasToNumber(Math.floor(Number(this.value) * grams))
          );
        },
      },
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        // minute: '%H:%M',
        hour: '%H:%M',
        day: '%e %b',
        week: '%d %b %y',
        month: '%b %Y',
        year: '%Y',
      },
    },
    credits: 'disabled',
    tooltip: {
      animation: true,
      borderRadius: 20,
      backgroundColor: 'white',
      borderWidth: 0,
      padding: 12,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const timestamp = this.x;
        const dateObj = new Date(timestamp as number);
        const time = formatAMPM(dateObj);
        return `<span style="color: var(--font-200);">${getShortDate(
          dateObj
        )}</span><br/><br/>
        <strong style="color: var(--font-500);">
        ${rate} ₹${addCommasToNumber(Math.floor(Number(this.y) * grams))} ${
          showTimeInToolTip
            ? `</strong><span style="color: var(--font-200); padding-left:8px;">${' '}${time}</span>`
            : ``
        }`;
      },
      style: { opacity: 1 },
    },

    series: [
      {
        data: [...goldSilverData],
      },
    ],
  };
};
